// Dashboard.tsx - URJA Main Application Dashboard
// Part of: AhantraVyuh Main Platform (URJA)
// Description: Central dashboard for energy mining, synthesis, wallet management, and ecosystem integration
// Location: /var/www/ahantravyuh/frontend/src/components/Dashboard.tsx

import React, { useState, useEffect } from 'react';
import { Zap, Wallet, LogOut, User, RefreshCw, Clock, TrendingUp, Beaker, History, Send, ArrowUpDown, ShoppingCart, X, Info, Globe, Gem, Mail, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MiningStatus, WalletData, MaterialsBalance, SynthesisHistory, TransferHistory } from '../types';
import EcycleRide from './EcycleRide';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const Dashboard: React.FC = () => {
  const { user, logout, token } = useAuth();
  const [miningStatus, setMiningStatus] = useState<MiningStatus>({ canMine: true, timeUntilNextMine: 0 });
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [walletData, setWalletData] = useState<WalletData>({
    urja_balance: user?.urja_balance || '0.00',
    ahant_balance: user?.ahant_balance || '0.00'
  });
  const [materialsBalance, setMaterialsBalance] = useState<MaterialsBalance>({ hydrogen: 0, rice: 0 });
  const [synthesisHistory, setSynthesisHistory] = useState<SynthesisHistory[]>([]);
  const [transferHistory, setTransferHistory] = useState<TransferHistory[]>([]);
  const [showSynthesisHistory, setShowSynthesisHistory] = useState(false);
  const [showTransferHistory, setShowTransferHistory] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [transferAmount, setTransferAmount] = useState('');

  useEffect(() => {
    checkMiningStatus();
    fetchMaterialsBalance();
    const interval = setInterval(checkMiningStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !miningStatus.canMine) {
      checkMiningStatus();
    }
  }, [countdown, miningStatus.canMine]);

  const checkMiningStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/mining-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setMiningStatus(data.data);
        if (!data.data.canMine) {
          setCountdown(data.data.timeUntilNextMine);
        }
      }
    } catch (error) {
      console.error('Failed to check mining status:', error);
    }
  };

  const fetchMaterialsBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/materials/balances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setMaterialsBalance(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch materials balance:', error);
    }
  };

  const fetchSynthesisHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/materials/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setSynthesisHistory(data.data.history);
      }
    } catch (error) {
      console.error('Failed to fetch synthesis history:', error);
    }
  };

  const fetchTransferHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/transfer/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setTransferHistory(data.data.history);
      }
    } catch (error) {
      console.error('Failed to fetch transfer history:', error);
    }
  };

  const refreshWallet = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/wallet`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setWalletData(data.data);
      }
      await fetchMaterialsBalance();
    } catch (error) {
      console.error('Failed to refresh wallet:', error);
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  const handleMine = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/mine-urja`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: `🎉 Successfully mined ${data.data.amount} URJA!` });
        setWalletData({ ...walletData, urja_balance: data.data.newBalance.toString() });
        await checkMiningStatus();
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Mining failed' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Mining failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSynthesizeHydrogen = async () => {
    const currentUrja = parseFloat(walletData.urja_balance);
    if (currentUrja < 100) {
      setMessage({ type: 'error', text: `⚠️ Insufficient URJA! Need 100 URJA, have ${currentUrja.toFixed(2)} URJA` });
      setTimeout(() => setMessage(null), 5000);
      return;
    }

    setSynthesizing(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/synthesize/hydrogen`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: `💎 Successfully synthesized 1 KG Hydrogen! Used ${data.data.synthesized.urja_spent} URJA` });
        setWalletData({
          ...walletData,
          urja_balance: data.data.balances.urja.toString()
        });
        await fetchMaterialsBalance();
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Synthesis failed' });
        setTimeout(() => setMessage(null), 5000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Synthesis failed. Please try again.' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSynthesizing(false);
    }
  };

  const handleTransferUrja = async () => {
    if (!recipientEmail || !transferAmount || parseFloat(transferAmount) <= 0) {
      setMessage({ type: 'error', text: '⚠️ Please enter valid recipient email and amount' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }

    if (parseFloat(transferAmount) > parseFloat(walletData.urja_balance)) {
      setMessage({ type: 'error', text: '⚠️ Insufficient URJA balance' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }

    setTransferring(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/transfer/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_email: recipientEmail,
          amount: parseFloat(transferAmount)
        })
      });
      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: `✅ Successfully transferred ${parseFloat(transferAmount).toFixed(2)} URJA to ${recipientEmail}!` });
        setWalletData({
          ...walletData,
          urja_balance: data.data.balances.urja.toString()
        });
        setRecipientEmail('');
        setTransferAmount('');
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Transfer failed' });
        setTimeout(() => setMessage(null), 5000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Transfer failed. Please try again.' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setTransferring(false);
    }
  };

  const toggleSynthesisHistory = async () => {
    if (!showSynthesisHistory) {
      await fetchSynthesisHistory();
    }
    setShowSynthesisHistory(!showSynthesisHistory);
  };

  const toggleTransferHistory = async () => {
    if (!showTransferHistory) {
      await fetchTransferHistory();
    }
    setShowTransferHistory(!showTransferHistory);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen animated-gradient-bg relative overflow-x-hidden">
      {/* Vibrant Animated Gradient Background - Same as Core Homepage */}
      <div className="color-splash-layer-1"></div>
      <div className="color-splash-layer-2"></div>

      {/* Header */}
      <header className="relative z-10 backdrop-blur-md bg-white/10 border-b border-white/20 sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-2 rounded-xl">
                <Zap className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">AhantraVyuh</h1>
                <p className="text-purple-200 text-xs">Welcome, {user?.name}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-xl transition-all border border-red-400/30"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl text-white backdrop-blur-lg ${
            message.type === 'success'
              ? 'bg-green-500/20 border-2 border-green-400/50'
              : 'bg-red-500/20 border-2 border-red-400/50'
          }`}>
            <p className="font-semibold">{message.text}</p>
          </div>
        )}

        {/* स्वर्णकोश (Golden Treasury) */}
        <div className="mb-8">
          <div className="backdrop-blur-xl bg-gradient-to-br from-amber-500/10 via-yellow-500/10 to-orange-500/10 rounded-3xl shadow-2xl p-8 border-2 border-amber-400/30">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-3 rounded-xl shadow-lg">
                  <Wallet className="text-white" size={28} />
                </div>
                

		<div>
                  <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-amber-200 to-orange-200">
                    Swarnakosh - Golden Treasury
                  </h2>
                  <p className="text-amber-200 text-sm">स्वर्णकोश</p>
                </div>


              </div>
              <button
                onClick={refreshWallet}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-amber-200 rounded-xl transition-all border border-amber-400/30"
              >
                <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* URJA Balance */}
              <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-md rounded-2xl p-6 border border-yellow-400/30">
                <div className="flex justify-between items-start mb-2">
                  <Zap className="text-yellow-400" size={32} />
                  <span className="bg-yellow-400/20 text-yellow-200 text-xs px-2 py-1 rounded-full">Energy</span>
                </div>
                <p className="text-4xl font-bold text-yellow-100 mb-1">{parseFloat(walletData.urja_balance).toFixed(2)}</p>
                <p className="text-yellow-300 text-sm font-semibold">URJA</p>
              </div>

              {/* AHANT Balance */}
              <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur-md rounded-2xl p-6 border border-amber-400/30">
                <div className="flex justify-between items-start mb-2">
                  <TrendingUp className="text-amber-400" size={32} />
                  <span className="bg-amber-400/20 text-amber-200 text-xs px-2 py-1 rounded-full">Tradeable</span>
                </div>
                <p className="text-4xl font-bold text-amber-100 mb-1">{parseFloat(walletData.ahant_balance).toFixed(2)}</p>
                <p className="text-amber-300 text-sm font-semibold">AHANT</p>
              </div>

              {/* Hydrogen Balance */}
              <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-md rounded-2xl p-6 border border-blue-400/30">
                <div className="flex justify-between items-start mb-2">
                  <Beaker className="text-blue-400" size={32} />
                  <span className="bg-blue-400/20 text-blue-200 text-xs px-2 py-1 rounded-full">Fuel</span>
                </div>
                <p className="text-4xl font-bold text-blue-100 mb-1">{materialsBalance.hydrogen.toFixed(4)}</p>
                <p className="text-blue-300 text-sm font-semibold">Hydrogen (KG)</p>
              </div>

              {/* Rice Balance */}
              <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-md rounded-2xl p-6 border border-green-400/30">
                <div className="flex justify-between items-start mb-2">
                  <ShoppingCart className="text-green-400" size={32} />
                  <span className="bg-green-400/20 text-green-200 text-xs px-2 py-1 rounded-full">Food</span>
                </div>
                <p className="text-4xl font-bold text-green-100 mb-1">{materialsBalance.rice.toFixed(4)}</p>
                <p className="text-green-300 text-sm font-semibold">Rice (KG)</p>
              </div>
            </div>
          </div>
        </div>

        {/* MEDHINI - Striking Section */}
        <div className="mb-8">
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-teal-500/20 via-cyan-500/20 to-blue-500/20 rounded-3xl shadow-2xl border-4 border-cyan-400/50 overflow-hidden">
            {/* Animated Globe Background Effect */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute w-64 h-64 bg-cyan-400 rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute right-0 bottom-0 w-64 h-64 bg-teal-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="relative z-10 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Left Side - Icon and Title */}
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-cyan-400 to-teal-500 p-4 rounded-2xl shadow-2xl animate-bounce">
                    <Globe className="text-white" size={48} />
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-teal-200 to-blue-200">
                      🌍 MEDHINI
                    </h2>
                    <p className="text-cyan-100 text-lg font-semibold">Living Digital Twin of the Earth</p>
                    <p className="text-cyan-300 text-sm mt-1">Collaborative 3D Globe • Real-Time Assets • Public Showcase</p>
                  </div>
                </div>

                {/* Right Side - CTA Button */}
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => window.location.href = '/medhini/'}
                    className="group relative px-8 py-4 bg-gradient-to-r from-cyan-400 via-teal-400 to-blue-400 hover:from-cyan-500 hover:via-teal-500 hover:to-blue-500 text-white font-bold text-xl rounded-2xl shadow-2xl transition-all transform hover:scale-110 hover:rotate-1 border-2 border-white/30"
                  >
                    <span className="flex items-center gap-3">
                      <Globe size={28} className="group-hover:animate-spin" />
                      Explore MEDHINI
                    </span>
                    <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-teal-400 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-200"></div>
                  </button>
                  <p className="text-cyan-200 text-xs text-center">
                    🏠 Set your home • 🗺️ View global assets • 🎯 Showcase to the world
                  </p>
                </div>
              </div>

              {/* Bottom Feature Pills */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-cyan-400/30">
                  <span className="text-cyan-100 text-sm font-medium">🌐 3D Interactive Globe</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-teal-400/30">
                  <span className="text-teal-100 text-sm font-medium">🏠 Home Location (1 AHANT)</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-400/30">
                  <span className="text-blue-100 text-sm font-medium">👥 Collaborative Earth</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-cyan-400/30">
                  <span className="text-cyan-100 text-sm font-medium">📍 Public Advertisement Board</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KHANIJ - Mining Game Section */}
        <div className="mb-8">
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-yellow-500/20 rounded-3xl shadow-2xl border-2 border-amber-400/40 overflow-hidden">
            {/* Animated Background Effect */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute w-64 h-64 bg-amber-400 rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute right-0 bottom-0 w-64 h-64 bg-orange-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="relative z-10 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Left Side - Icon and Title */}
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-4 rounded-2xl shadow-2xl animate-bounce">
                    <Gem className="text-white" size={48} />
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-orange-200 to-yellow-200">
                      ⛏️ KHANIJ
                    </h2>
                    <p className="text-amber-100 text-lg font-semibold">Digital Twin of Earth's Minerals</p>
                    <p className="text-amber-300 text-sm mt-1">Mine Elements • Use URJA • Build Your Collection</p>
                  </div>
                </div>

                {/* Right Side - CTA Button */}
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => window.location.href = '/khanij/'}
                    className="group relative px-8 py-4 bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 hover:from-amber-500 hover:via-orange-500 hover:to-yellow-500 text-white font-bold text-xl rounded-2xl shadow-2xl transition-all transform hover:scale-110 hover:rotate-1 border-2 border-white/30"
                  >
                    <span className="flex items-center gap-3">
                      <Gem size={28} className="group-hover:animate-spin" />
                      Start Mining
                    </span>
                    <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-200"></div>
                  </button>
                  <p className="text-amber-200 text-xs text-center">
                    ⚡ 10 URJA = 1 kg Copper • 💎 Real-world data simulation
                  </p>
                </div>
              </div>

              {/* Bottom Feature Pills */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-amber-400/30">
                  <span className="text-amber-100 text-sm font-medium">🌍 Real Mineral Data</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-orange-400/30">
                  <span className="text-orange-100 text-sm font-medium">⚡ URJA Powered</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-400/30">
                  <span className="text-yellow-100 text-sm font-medium">📊 Periodic Table</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-amber-400/30">
                  <span className="text-amber-100 text-sm font-medium">🏆 Build Collection</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* DOOTA - Email Service Section */}
        <div className="mb-8">
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-emerald-500/20 via-green-500/20 to-teal-500/20 rounded-3xl shadow-2xl border-2 border-emerald-400/40 overflow-hidden">
            {/* Animated Background Effect */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute w-64 h-64 bg-emerald-400 rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute right-0 bottom-0 w-64 h-64 bg-teal-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="relative z-10 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Left Side - Icon and Title */}
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-emerald-400 to-green-500 p-4 rounded-2xl shadow-2xl animate-bounce">
                    <Mail className="text-white" size={48} />
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 via-green-200 to-teal-200">
                      📧 DOOTA
                    </h2>
                    <p className="text-emerald-100 text-lg font-semibold">Email-as-a-Service Platform</p>
                    <p className="text-emerald-300 text-sm mt-1">Free @ahantravyuh.com • IMAP/SMTP • Secure Mailbox</p>
                  </div>
                </div>

                {/* Right Side - CTA Button */}
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => window.location.href = '/doota/'}
                    className="group relative px-8 py-4 bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400 hover:from-emerald-500 hover:via-green-500 hover:to-teal-500 text-white font-bold text-xl rounded-2xl shadow-2xl transition-all transform hover:scale-110 hover:rotate-1 border-2 border-white/30"
                  >
                    <span className="flex items-center gap-3">
                      <Mail size={28} className="group-hover:animate-spin" />
                      Access Email
                    </span>
                    <div className="absolute -inset-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-200"></div>
                  </button>
                  <p className="text-emerald-200 text-xs text-center">
                    ✉️ Your @ahantravyuh.com mailbox • दूत (Messenger)
                  </p>
                </div>
              </div>

              {/* Bottom Feature Pills */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-emerald-400/30">
                  <span className="text-emerald-100 text-sm font-medium">📬 Free Email Address</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-green-400/30">
                  <span className="text-green-100 text-sm font-medium">🔐 Secure Mailbox</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-teal-400/30">
                  <span className="text-teal-100 text-sm font-medium">📨 IMAP/SMTP Access</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-emerald-400/30">
                  <span className="text-emerald-100 text-sm font-medium">🌐 mail.ahantravyuh.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BHOOMI - Land Registry Section */}
        <div className="mb-8">
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-green-600/20 via-emerald-600/20 to-lime-600/20 rounded-3xl shadow-2xl border-2 border-green-500/40 overflow-hidden">
            {/* Animated Background Effect */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute w-64 h-64 bg-green-500 rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute right-0 bottom-0 w-64 h-64 bg-lime-500 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="relative z-10 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Left Side - Icon and Title */}
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-green-500 to-lime-600 p-4 rounded-2xl shadow-2xl animate-bounce">
                    <MapPin className="text-white" size={48} />
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-200 via-emerald-200 to-lime-200">
                      🗺️ BHOOMI
                    </h2>
                    <p className="text-green-100 text-lg font-semibold">Hybrid Physical/Digital Land Registry</p>
                    <p className="text-green-300 text-sm mt-1">Interactive Display • Real Estate Mapping • Land Records</p>
                  </div>
                </div>

                {/* Right Side - CTA Button */}
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => window.location.href = '/bhoomi/'}
                    className="group relative px-8 py-4 bg-gradient-to-r from-green-500 via-emerald-500 to-lime-500 hover:from-green-600 hover:via-emerald-600 hover:to-lime-600 text-white font-bold text-xl rounded-2xl shadow-2xl transition-all transform hover:scale-110 hover:rotate-1 border-2 border-white/30"
                  >
                    <span className="flex items-center gap-3">
                      <MapPin size={28} className="group-hover:animate-spin" />
                      View Land Registry
                    </span>
                    <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-lime-500 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-200"></div>
                  </button>
                  <p className="text-green-200 text-xs text-center">
                    🏞️ Physical + Digital • भूमि (Land)
                  </p>
                </div>
              </div>

              {/* Bottom Feature Pills */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-green-500/30">
                  <span className="text-green-100 text-sm font-medium">📍 Interactive Map Display</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-emerald-500/30">
                  <span className="text-emerald-100 text-sm font-medium">🏘️ Land Registry Records</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-lime-500/30">
                  <span className="text-lime-100 text-sm font-medium">🔗 Hybrid Physical/Digital</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-green-500/30">
                  <span className="text-green-100 text-sm font-medium">🗺️ Real Estate Mapping</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* GYANODBHAW - University Section */}
        <div className="mb-8">
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-purple-500/20 via-indigo-500/20 to-blue-500/20 rounded-3xl shadow-2xl border-2 border-purple-400/40 overflow-hidden">
            <div className="relative z-10 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-purple-400 to-indigo-500 p-4 rounded-2xl shadow-2xl">
                    <svg className="text-white w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-indigo-200 to-blue-200">
                      GYANODBHAW
                    </h2>
                    <p className="text-purple-100 text-lg font-semibold">The Seed of Knowledge - University</p>
                    <p className="text-purple-300 text-sm mt-1">Coming Soon - Learn, Grow, Achieve</p>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => window.location.href = '/gyanodbhaw/'}
                    className="group relative px-8 py-4 bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400 hover:from-purple-500 hover:via-indigo-500 hover:to-blue-500 text-white font-bold text-xl rounded-2xl shadow-2xl transition-all transform hover:scale-110 hover:rotate-1 border-2 border-white/30"
                  >
                    <span className="flex items-center gap-3">
                      Explore
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VED - App Directory Section */}
        <div className="mb-8">
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-violet-500/20 via-fuchsia-500/20 to-pink-500/20 rounded-3xl shadow-2xl border-4 border-fuchsia-400/50 overflow-hidden">
            {/* Animated Background Effect */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute w-64 h-64 bg-violet-400 rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute right-0 bottom-0 w-64 h-64 bg-pink-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="relative z-10 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Left Side - Icon and Title */}
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-violet-400 to-fuchsia-500 p-4 rounded-2xl shadow-2xl animate-bounce">
                    <svg className="text-white w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-200 via-fuchsia-200 to-pink-200">
                      🎯 VED APPS
                    </h2>
                    <p className="text-violet-100 text-lg font-semibold">Complete Application Directory</p>
                    <p className="text-fuchsia-300 text-sm mt-1">1,021 Apps • All Categories • Instant Access</p>
                  </div>
                </div>

                {/* Right Side - CTA Button */}
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => window.location.href = '/ved/ved'}
                    className="group relative px-8 py-4 bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 hover:from-violet-500 hover:via-fuchsia-500 hover:to-pink-500 text-white font-bold text-xl rounded-2xl shadow-2xl transition-all transform hover:scale-110 hover:rotate-1 border-2 border-white/30"
                  >
                    <span className="flex items-center gap-3">
                      <svg className="w-7 h-7 group-hover:animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      Browse Directory
                    </span>
                    <div className="absolute -inset-1 bg-gradient-to-r from-violet-400 to-pink-400 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-200"></div>
                  </button>
                  <p className="text-fuchsia-200 text-xs text-center">
                    📱 Energy • Mining • Games • Education • Business
                  </p>
                </div>
              </div>

              {/* Bottom Feature Pills */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-violet-400/30">
                  <span className="text-violet-100 text-sm font-medium">🌐 1,021 Applications</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-fuchsia-400/30">
                  <span className="text-fuchsia-100 text-sm font-medium">🎮 Games & Simulations</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-pink-400/30">
                  <span className="text-pink-100 text-sm font-medium">⚡ Energy & Resources</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-violet-400/30">
                  <span className="text-violet-100 text-sm font-medium">🔐 Unified Authentication</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VEDAS - Sacred Texts Section */}
        <div className="mb-8">
          <div className="relative backdrop-blur-xl bg-gradient-to-br from-amber-500/20 via-yellow-500/20 to-orange-500/20 rounded-3xl shadow-2xl border-4 border-amber-400/50 overflow-hidden">
            {/* Animated Background Effect */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute w-64 h-64 bg-amber-400 rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute right-0 bottom-0 w-64 h-64 bg-orange-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="relative z-10 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Left Side - Icon and Title */}
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-4 rounded-2xl shadow-2xl animate-bounce">
                    <svg className="text-white w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-200 to-orange-200">
                      वेद • VEDAS
                    </h2>
                    <p className="text-amber-100 text-lg font-semibold">Sacred Ancient Texts</p>
                    <p className="text-amber-300 text-sm mt-1">4 Vedas • Sanskrit • English Translations</p>
                  </div>
                </div>

                {/* Right Side - Veda Links Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => window.location.href = '/rigveda/'}
                    className="group relative px-6 py-3 bg-gradient-to-r from-orange-400 to-red-400 hover:from-orange-500 hover:to-red-500 text-white font-bold text-sm rounded-xl shadow-xl transition-all transform hover:scale-105 border border-white/30"
                  >
                    <span className="flex items-center gap-2">
                      ऋग्वेद
                    </span>
                  </button>
                  <button
                    onClick={() => window.location.href = '/yajurveda/'}
                    className="group relative px-6 py-3 bg-gradient-to-r from-purple-400 to-indigo-400 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm rounded-xl shadow-xl transition-all transform hover:scale-105 border border-white/30"
                  >
                    <span className="flex items-center gap-2">
                      यजुर्वेद
                    </span>
                  </button>
                  <button
                    onClick={() => window.location.href = '/samaveda/'}
                    className="group relative px-6 py-3 bg-gradient-to-r from-green-400 to-teal-400 hover:from-green-500 hover:to-teal-500 text-white font-bold text-sm rounded-xl shadow-xl transition-all transform hover:scale-105 border border-white/30"
                  >
                    <span className="flex items-center gap-2">
                      सामवेद
                    </span>
                  </button>
                  <button
                    onClick={() => window.location.href = '/atharvaveda/'}
                    className="group relative px-6 py-3 bg-gradient-to-r from-cyan-400 to-sky-400 hover:from-cyan-500 hover:to-sky-500 text-white font-bold text-sm rounded-xl shadow-xl transition-all transform hover:scale-105 border border-white/30"
                  >
                    <span className="flex items-center gap-2">
                      अथर्ववेद
                    </span>
                  </button>
                </div>
              </div>

              {/* Bottom Feature Pills */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-orange-400/30">
                  <span className="text-orange-100 text-sm font-medium">📖 Rigveda - Hymns of Knowledge</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-purple-400/30">
                  <span className="text-purple-100 text-sm font-medium">🔥 Yajurveda - Rituals</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-green-400/30">
                  <span className="text-green-100 text-sm font-medium">🎵 Samaveda - Melodies</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-cyan-400/30">
                  <span className="text-cyan-100 text-sm font-medium">🔮 Atharvaveda - Wisdom</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 1: URJA Mining + Buy AHANT + Hydrogen Synthesis + URJA Transfer */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* URJA Mining Section */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-3xl shadow-2xl p-8 border border-white/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-3 rounded-xl">
                <Zap className="text-white" size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Mine URJA</h2>
                <p className="text-purple-200 text-sm">Earn energy tokens</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-yellow-400/30">
              {miningStatus.canMine ? (
                <div>
                  <p className="text-white text-center mb-2">Ready to mine!</p>
                  <div className="flex items-center justify-center">
                    <Zap className="text-yellow-400 animate-pulse" size={48} />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-sm">Next mine available in:</span>
                    <Clock className="text-purple-300" size={20} />
                  </div>
                  <p className="text-3xl font-bold text-yellow-400 text-center font-mono">
                    {formatTime(countdown)}
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={() => { if (miningStatus.canMine) window.open('/dravya/', '_blank'); }}
              disabled={!miningStatus.canMine}
              className={`w-full py-4 rounded-2xl font-bold text-lg transition-all transform shadow-2xl flex items-center justify-center gap-3 ${
                miningStatus.canMine
                  ? 'bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-500 hover:via-orange-600 hover:to-red-600 text-white hover:scale-[1.02]'
                  : 'bg-gray-600/30 text-gray-400 cursor-not-allowed opacity-60'
              }`}
            >
              <Zap size={24} />
              {miningStatus.canMine ? '⚡ Urja Boost' : '⏳ Cooldown Active'}
            </button>
          </div>

          {/* Hydrogen Synthesis Section */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-3xl shadow-2xl p-8 border border-white/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-br from-blue-400 to-cyan-500 p-3 rounded-xl">
                <Beaker className="text-white" size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Synthesize H₂</h2>
                <p className="text-purple-200 text-sm">Convert energy to fuel</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-blue-400/30">
              <p className="text-white text-sm mb-2">
                <strong>Cost:</strong> 100 URJA → 1 KG Hydrogen
              </p>
              <p className="text-purple-200 text-sm">
                Use Hydrogen as fuel for vehicles in Waahan app!
              </p>
            </div>

            <button
              onClick={handleSynthesizeHydrogen}
              disabled={synthesizing || parseFloat(walletData.urja_balance) < 100}
              className={`w-full py-4 rounded-2xl font-bold text-lg transition-all transform shadow-2xl flex items-center justify-center gap-3 mb-4 ${
                !synthesizing && parseFloat(walletData.urja_balance) >= 100
                  ? 'bg-gradient-to-r from-blue-400 via-cyan-500 to-teal-500 hover:from-blue-500 hover:via-cyan-600 hover:to-teal-600 text-white hover:scale-[1.02]'
                  : 'bg-gray-600/30 text-gray-400 cursor-not-allowed opacity-60'
              }`}
            >
              <Beaker size={24} className={synthesizing ? 'animate-spin' : ''} />
              {synthesizing ? 'Synthesizing...' : parseFloat(walletData.urja_balance) >= 100 ? '🔬 Synthesize 1 KG H₂' : '⚠️ Need 100 URJA'}
            </button>

            {/* History Button */}
            <button
              onClick={toggleSynthesisHistory}
              className="w-full py-2 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20"
            >
              <History size={16} />
              {showSynthesisHistory ? 'Hide' : 'Show'} History
            </button>

            {/* Synthesis History */}
            {showSynthesisHistory && (
              <div className="mt-4 bg-white/5 backdrop-blur-sm rounded-xl p-4 max-h-60 overflow-y-auto">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <History size={16} />
                  History
                </h3>
                {synthesisHistory.length === 0 ? (
                  <p className="text-purple-200 text-sm text-center py-4">No history yet</p>
                ) : (
                  <div className="space-y-2">
                    {synthesisHistory.map((item, index) => (
                      <div key={index} className="bg-white/5 rounded-lg p-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-white font-medium">{parseFloat(item.amount_kg).toFixed(4)} KG H₂</span>
                          <span className="text-yellow-300">-{parseFloat(item.urja_cost).toFixed(0)} URJA</span>
                        </div>
                        <div className="text-purple-300 text-xs mt-1">
                          {new Date(item.synthesized_at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* URJA Transfer Section */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-3xl shadow-2xl p-8 border border-white/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-br from-pink-400 to-purple-500 p-3 rounded-xl">
                <Send className="text-white" size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Transfer URJA</h2>
                <p className="text-purple-200 text-sm">Send to others</p>
              </div>
            </div>

            {/* Transfer Form */}
            <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-pink-400/30">
              <div className="mb-4">
                <label className="block text-white font-semibold text-sm mb-2">Recipient Email</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-pink-400"
                />
              </div>
              <div className="mb-4">
                <label className="block text-white font-semibold text-sm mb-2">Amount (URJA)</label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-pink-400"
                />
              </div>
              <p className="text-purple-200 text-sm">
                {transferAmount && parseFloat(transferAmount) > parseFloat(walletData.urja_balance)
                  ? `⚠️ Insufficient URJA! You have ${parseFloat(walletData.urja_balance).toFixed(2)} URJA`
                  : '💸 Enter recipient and amount to transfer'}
              </p>
            </div>

            {/* Transfer Button */}
            <button
              onClick={handleTransferUrja}
              disabled={transferring || !recipientEmail || !transferAmount || parseFloat(transferAmount) <= 0 || parseFloat(transferAmount) > parseFloat(walletData.urja_balance)}
              className={`w-full py-4 rounded-2xl font-bold text-lg transition-all transform shadow-2xl flex items-center justify-center gap-3 mb-4 ${
                !transferring && recipientEmail && transferAmount && parseFloat(transferAmount) > 0 && parseFloat(transferAmount) <= parseFloat(walletData.urja_balance)
                  ? 'bg-gradient-to-r from-pink-400 via-purple-500 to-indigo-500 hover:from-pink-500 hover:via-purple-600 hover:to-indigo-600 text-white hover:scale-[1.02]'
                  : 'bg-gray-600/30 text-gray-400 cursor-not-allowed opacity-60'
              }`}
            >
              <Send size={24} className={transferring ? 'animate-spin' : ''} />
              {transferring ? 'Transferring...' : '💸 Transfer URJA'}
            </button>

            {/* History Button */}
            <button
              onClick={toggleTransferHistory}
              className="w-full py-2 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20"
            >
              <ArrowUpDown size={16} />
              {showTransferHistory ? 'Hide' : 'Show'} History
            </button>

            {/* Transfer History */}
            {showTransferHistory && (
              <div className="mt-4 bg-white/5 backdrop-blur-sm rounded-xl p-4 max-h-60 overflow-y-auto">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <ArrowUpDown size={16} />
                  History
                </h3>
                {transferHistory.length === 0 ? (
                  <p className="text-purple-200 text-sm text-center py-4">No transfers yet</p>
                ) : (
                  <div className="space-y-2">
                    {transferHistory.map((item, index) => (
                      <div key={index} className={`bg-white/5 rounded-lg p-3 text-sm border ${
                        item.transfer_type === 'sent' ? 'border-red-400/30' : 'border-green-400/30'
                      }`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`font-bold text-xs ${
                            item.transfer_type === 'sent' ? 'text-red-300' : 'text-green-300'
                          }`}>
                            {item.transfer_type === 'sent' ? '📤 SENT' : '📥 RECEIVED'}
                          </span>
                          <span className={`font-bold ${
                            item.transfer_type === 'sent' ? 'text-red-300' : 'text-green-300'
                          }`}>
                            {item.transfer_type === 'sent' ? '-' : '+'}{parseFloat(item.amount).toFixed(2)} URJA
                          </span>
                        </div>
                        <div className="text-white text-xs">
                          {item.transfer_type === 'sent' ? 'To: ' : 'From: '}
                          {item.transfer_type === 'sent' ? item.recipient_name : item.sender_name}
                        </div>
                        <div className="text-purple-300 text-xs mt-1">
                          {new Date(item.transferred_at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: E-Cycle Ride - Full Width */}
        <div className="mb-8">
          <EcycleRide
            token={token || ''}
            onUpdate={() => {
              refreshWallet();
              fetchMaterialsBalance();
            }}
          />
        </div>


{/* Collaborate With Us Section */}
        <div className="mb-8">
          <div className="backdrop-blur-xl bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl shadow-2xl p-8 border-2 border-purple-400/50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Left Side - Content */}
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-pink-200 to-indigo-200 mb-3">
                  🤝 Collaborate With AhantraVyuh
                </h2>
                <p className="text-purple-100 text-lg mb-4">
                  Have an idea? We'll build it for you!
                </p>
                
                {/* Key Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-xl border border-purple-400/30">
                    <span className="text-purple-100 text-sm">💡 Bring your concept</span>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-xl border border-pink-400/30">
                    <span className="text-pink-100 text-sm">🎨 Create content & branding</span>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-xl border border-indigo-400/30">
                    <span className="text-indigo-100 text-sm">📣 Market to the world</span>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-xl border border-purple-400/30">
                    <span className="text-purple-100 text-sm">⚙️ We handle all tech</span>
                  </div>
                </div>

                <p className="text-purple-200 text-sm">
                  Join 230+ users in our growing ecosystem. Your app integrates with our platform, currency system, and MEDHINI's 3D Earth visualization.
                </p>
              </div>

              {/* Right Side - CTA */}
              <div className="flex flex-col items-center gap-3">
                <button
                  onClick={() => window.open('/collaborate.html', '_blank')}
                  className="group relative px-8 py-4 bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 hover:from-purple-500 hover:via-pink-500 hover:to-indigo-500 text-white font-bold text-lg rounded-2xl shadow-2xl transition-all transform hover:scale-110 border-2 border-white/30"
                >
                  <span className="flex items-center gap-3">
                    🚀 Learn More
                  </span>
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-200"></div>
                </button>
                <p className="text-purple-200 text-xs text-center">
                  All-inclusive technology platform
                </p>
              </div>
            </div>
          </div>
        </div>


        {/* User Info Card */}
        <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-3xl shadow-2xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <User size={20} />
            Account Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
              <p className="text-purple-300 text-xs mb-1">Name</p>
              <p className="text-white font-semibold">{user?.name}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
              <p className="text-purple-300 text-xs mb-1">Email</p>
              <p className="text-white font-semibold text-sm">{user?.email}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
              <p className="text-purple-300 text-xs mb-1">AHN-ID</p>
              <p className="text-white font-mono text-xs break-all">{user?.ahn_id}</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative mt-12 py-6 text-center text-purple-300 text-sm backdrop-blur-md bg-white/5 border-t border-white/10">
        <p>AHANTRAVYUH</p>
        <p className="text-xs text-purple-400 mt-1">Every aspiration, thought and deed are all but flowers at her feet.</p>
      </footer>

      {/* Vibrant Animated Gradient Styles */}
      <style>{`
        /* Vibrant Animated Gradient Background */
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          25% { background-position: 100% 50%; }
          50% { background-position: 50% 100%; }
          75% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes colorSplash {
          0%, 100% { opacity: 0.4; transform: scale(1) rotate(0deg); }
          50% { opacity: 0.7; transform: scale(1.2) rotate(180deg); }
        }

        .animated-gradient-bg {
          background: linear-gradient(-45deg,
            #ff006e, /* Hot Pink */
            #fb5607, /* Orange */
            #ffbe0b, /* Yellow */
            #8338ec, /* Purple */
            #3a86ff, /* Blue */
            #06ffa5, /* Mint Green */
            #ff006e, /* Hot Pink */
            #fb5607  /* Orange */
          );
          background-size: 400% 400%;
          animation: gradientShift 20s ease infinite;
          position: relative;
        }

        .color-splash-layer-1 {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image:
            radial-gradient(circle at 10% 20%, rgba(255, 0, 110, 0.3) 0%, transparent 25%),
            radial-gradient(circle at 90% 80%, rgba(251, 86, 7, 0.3) 0%, transparent 25%),
            radial-gradient(circle at 50% 50%, rgba(255, 190, 11, 0.25) 0%, transparent 30%),
            radial-gradient(circle at 80% 20%, rgba(131, 56, 236, 0.3) 0%, transparent 25%),
            radial-gradient(circle at 20% 80%, rgba(58, 134, 255, 0.3) 0%, transparent 25%),
            radial-gradient(circle at 70% 60%, rgba(6, 255, 165, 0.25) 0%, transparent 30%);
          animation: colorSplash 8s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }

        .color-splash-layer-2 {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image:
            radial-gradient(circle at 40% 40%, rgba(255, 0, 110, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 60% 70%, rgba(251, 86, 7, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 30% 60%, rgba(131, 56, 236, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 75% 35%, rgba(58, 134, 255, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 85% 85%, rgba(6, 255, 165, 0.25) 0%, transparent 20%),
            radial-gradient(circle at 15% 15%, rgba(255, 190, 11, 0.25) 0%, transparent 20%);
          animation: colorSplash 10s ease-in-out infinite reverse;
          pointer-events: none;
          z-index: 0;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
